/**
 * =============================================================================
 * ROASTBREW - AUTHENTICATION UI
 * =============================================================================
 *
 * Luxury authentication interface with landing page, login, registration,
 * forgot password, and resend verification. Email and password only (Google and
 * Apple sign-in were removed on October 21, 2026: RoastBrew uses its own login).
 *
 * VIEWS:
 * - landing: Welcome screen with CTA buttons
 * - login: Email/password form → POST /auth/login
 * - register: Name/email/password form → POST /auth/register
 * - verify-sent: Confirmation screen after registration (with resend button)
 * - forgot-password: Email form → POST /auth/forgot-password
 * - forgot-sent: Confirmation screen after forgot password request
 *
 * CONNECTED TO:
 * - App.jsx: AuthContext provides login(), loginWithResponse(), API_URL
 * - react-router-dom: Uses useNavigate for page transitions
 *
 * THEME: pre-auth, so it takes its colours from publicTheme.js — not
 * ThemeContext, which has no tier to read before sign-in. The `landing` view
 * renders LandingPageObs, which mounts <PublicTheme/> too; that is safe and
 * deliberate (the theme is reference counted — see publicTheme.js).
 *
 * =============================================================================
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getDeviceInfo } from './utils/device';
import LandingPageObs from './LandingPageObs';
import { T, PublicTheme } from './publicTheme';
import PublicMark from './PublicMark';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function AuthUI() {
  const navigate = useNavigate();
  const { login, loginWithResponse, API_URL } = useAuth();

  // ---------------------------------------------------------------------------
  // INTERNAL STATE
  // ---------------------------------------------------------------------------

  const [view, setView] = useState('landing');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // ---------------------------------------------------------------------------
  // SCROLL POSITION ACROSS VIEWS
  // ---------------------------------------------------------------------------
  // The views are one route, so App.jsx's ScrollToTop never fires between them.
  // Without this, tapping "Start learning" at the bottom of the landing page
  // opened the form still scrolled down — the Back link and heading sat hidden
  // under the fixed wordmark bar. Each form view now opens at the top, and
  // coming Back to the landing page returns to where the visitor left it.
  // Layout effect, so the jump happens before paint (no flash of the old spot).
  const landingScrollRef = useRef(0);
  const prevViewRef = useRef(view);

  useLayoutEffect(() => {
    const prev = prevViewRef.current;
    if (prev === view) return;
    prevViewRef.current = view;

    if (view === 'landing') {
      window.scrollTo(0, landingScrollRef.current);
    } else {
      window.scrollTo(0, 0);
    }
  }, [view]);

  // Remember the landing position at the moment a CTA switches away from it.
  const leaveLanding = (next) => {
    landingScrollRef.current = window.scrollY;
    setError('');
    setView(next);
  };

  // ---------------------------------------------------------------------------
  // RESEND COOLDOWN TIMER
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // ---------------------------------------------------------------------------
  // EMAIL/PASSWORD HANDLERS
  // ---------------------------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const device = getDeviceInfo();

      if (view === 'login') {
        const response = await fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            device,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || data.message || 'Invalid email or password');
        }

        loginWithResponse(data);

      } else {
        const response = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            displayName: formData.name,
            device,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || data.message || 'Registration failed');
        }

        setView('verify-sent');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // FORGOT PASSWORD
  // ---------------------------------------------------------------------------

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to send reset email');
      }

      setView('forgot-sent');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // RESEND VERIFICATION
  // ---------------------------------------------------------------------------

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to resend verification');
      }

      setResendCooldown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };


  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <div
      className="rb-public"
      style={{
        // LandingPageObs is a full, SCROLLING page that paints its own field.
        // The form views get the centred-card treatment instead.
        //
        // NOTE: this used to set `overflow: hidden` for the form views. On a
        // short viewport that clipped the taller register form and made its
        // submit button unreachable — there was no way to scroll to it. Nothing
        // needed the clip, so it is gone; `.rb-public` still pins overflow-x.
        overflow: 'visible',
      }}
    >
      <PublicTheme />

      {/* Page chrome — ambient field and the fixed wordmark. Hidden for the
          landing view: LandingPageObs brings its own nav and background, and
          two overlapping navs is exactly the incoherence this was meant to
          fix. */}
      {view !== 'landing' && (
        <>
          <div className="rb-ambient" />

          <nav style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 100,
            background: 'rgba(13,13,13,0.72)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            borderBottom: `.5px solid ${T.hairline}`,
          }}>
            <PublicMark />
          </nav>
        </>
      )}

      {/* =================================================================
          MAIN CONTENT AREA
          ================================================================= */}
      {/* LandingPageObs manages its own layout, so main gets out of the way
          entirely for that view. (alignItems:center on a taller-than-viewport
          child clips it at BOTH ends and makes the top unreachable, so the
          form views use a top-aligned flex with generous padding instead.) */}
      <main style={view === 'landing' ? { minHeight: '100vh' } : {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '112px 24px 64px',
      }}>

        {/* ===============================================================
            LANDING VIEW — the real landing page, not a copy of it
            ===============================================================
            This renders LandingPageObs itself. An earlier version rebuilt a thin
            imitation here — same class names, cut-down copy, fewer sections —
            which meant two pages to keep in step and a visibly worse one at the
            front door.

            LandingPageObs takes onGetStarted/onSignIn as props and imports
            nothing from the router or auth context, so it drops straight in. The
            CTAs switch this component's own view instead of navigating:

              onGetStarted -> the register form
              onSignIn     -> the login form

            Result: a visitor gets the full landing page, and signing in is one
            click from the hero. The form views below are unchanged.
            =============================================================== */}
        {view === 'landing' && (
          <LandingPageObs
            onGetStarted={() => leaveLanding('register')}
            onSignIn={() => leaveLanding('login')}
          />
        )}

        {/* ===============================================================
            LOGIN / REGISTER FORM VIEW
            =============================================================== */}
        {(view === 'login' || view === 'register') && (
          <div className="rb-enter" style={{ width: '100%', maxWidth: 'var(--m-form)' }}>
            <BackLink onClick={() => { setError(''); setView('landing'); }}>
              Back
            </BackLink>

            <h1 className="rb-h">
              {view === 'login' ? 'Welcome back' : 'Create your account'}
            </h1>
            <p className="rb-note" style={{ marginBottom: 'var(--s-4)' }}>
              {view === 'login'
                ? 'Pick up where you left off.'
                : 'Free tier, no card required.'}
            </p>

            {error && (
              <div className="rb-alert rb-alert--error" style={{ marginBottom: 'var(--s-3)' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Name Field — register only */}
              {view === 'register' && (
                <div className="rb-field">
                  <label className="rb-label" htmlFor="rb-name">Full name</label>
                  <input
                    id="rb-name"
                    className="rb-input"
                    type="text"
                    autoComplete="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
              )}

              {/* Email Field */}
              <div className="rb-field">
                <label className="rb-label" htmlFor="rb-email">Email address</label>
                <input
                  id="rb-email"
                  className="rb-input"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              {/* Password Field */}
              <div className="rb-field" style={{ marginBottom: view === 'login' ? '10px' : 'var(--s-4)' }}>
                <label className="rb-label" htmlFor="rb-password">Password</label>
                <input
                  id="rb-password"
                  className="rb-input"
                  type="password"
                  autoComplete={view === 'login' ? 'current-password' : 'new-password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                />
              </div>

              {/* Forgot Password Link (login only) */}
              {view === 'login' && (
                <div style={{ marginBottom: 'var(--s-4)', textAlign: 'right' }}>
                  <button
                    type="button"
                    className="rb-link rb-link--quiet"
                    style={{ fontSize: 'var(--t-small)' }}
                    onClick={() => { setError(''); setView('forgot-password'); }}
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                className="rb-btn rb-btn--gold rb-btn--block"
                disabled={isLoading}
                data-busy={isLoading ? 'true' : undefined}
              >
                {isLoading ? (
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                  }}>
                    <span
                      className="rb-spinner"
                      style={{
                        width: '14px',
                        height: '14px',
                        borderColor: 'rgba(13,13,13,0.25)',
                        borderTopColor: 'currentColor',
                      }}
                    />
                    Processing
                  </span>
                ) : (
                  view === 'login' ? 'Sign in' : 'Create account'
                )}
              </button>
            </form>

            {/* Toggle Link */}
            <p className="rb-note" style={{ marginTop: 'var(--s-3)', textAlign: 'center' }}>
              {view === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button
                className="rb-link"
                onClick={() => {
                  setError('');
                  setView(view === 'login' ? 'register' : 'login');
                }}
              >
                {view === 'login' ? 'Create one' : 'Sign in'}
              </button>
            </p>
          </div>
        )}

        {/* ===============================================================
            VERIFY EMAIL SENT VIEW
            =============================================================== */}
        {view === 'verify-sent' && (
          <div className="rb-enter" style={{ textAlign: 'center', maxWidth: '480px', width: '100%' }}>
            <EnvelopeGlyph />

            <h1 className="rb-h">Check your email</h1>
            <p className="rb-note rb-note--lead" style={{ marginBottom: 'var(--s-1)' }}>
              We&rsquo;ve sent a verification link to
            </p>
            <p style={{
              fontFamily: T.body,
              fontSize: 'var(--t-lead)',
              color: T.parchment,
              fontWeight: 500,
              margin: '0 0 var(--s-3)',
              wordBreak: 'break-word',
            }}>
              {formData.email}
            </p>
            <p className="rb-note" style={{ marginBottom: 'var(--s-4)' }}>
              Open the link to verify your account, then come back here to sign in.
            </p>

            {error && (
              <div className="rb-alert rb-alert--error" style={{ marginBottom: 'var(--s-3)' }}>
                {error}
              </div>
            )}

            <button
              className="rb-btn rb-btn--gold"
              onClick={handleResendVerification}
              disabled={isLoading || resendCooldown > 0}
              data-busy={isLoading ? 'true' : undefined}
            >
              {resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : isLoading ? 'Sending' : 'Resend email'}
            </button>

            <p style={{ margin: 'var(--s-3) 0 0' }}>
              <button
                className="rb-link rb-link--quiet"
                style={{ fontSize: 'var(--t-small)' }}
                onClick={() => { setError(''); setView('login'); }}
              >
                Back to sign in
              </button>
            </p>
          </div>
        )}

        {/* ===============================================================
            FORGOT PASSWORD VIEW
            =============================================================== */}
        {view === 'forgot-password' && (
          <div className="rb-enter" style={{ width: '100%', maxWidth: 'var(--m-form)' }}>
            <BackLink onClick={() => { setError(''); setView('login'); }}>
              Back to sign in
            </BackLink>

            <h1 className="rb-h">Reset your password</h1>
            <p className="rb-note" style={{ marginBottom: 'var(--s-4)' }}>
              Enter your email and we&rsquo;ll send you a link to set a new one.
            </p>

            {error && (
              <div className="rb-alert rb-alert--error" style={{ marginBottom: 'var(--s-3)' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleForgotPassword}>
              <div className="rb-field" style={{ marginBottom: 'var(--s-4)' }}>
                <label className="rb-label" htmlFor="rb-forgot-email">Email address</label>
                <input
                  id="rb-forgot-email"
                  className="rb-input"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <button
                type="submit"
                className="rb-btn rb-btn--gold rb-btn--block"
                disabled={isLoading}
                data-busy={isLoading ? 'true' : undefined}
              >
                {isLoading ? 'Sending' : 'Send reset link'}
              </button>
            </form>
          </div>
        )}

        {/* ===============================================================
            FORGOT PASSWORD SENT VIEW
            =============================================================== */}
        {view === 'forgot-sent' && (
          <div className="rb-enter" style={{ textAlign: 'center', maxWidth: '480px', width: '100%' }}>
            <EnvelopeGlyph />

            <h1 className="rb-h">Check your email</h1>
            <p className="rb-note rb-note--lead" style={{ marginBottom: 'var(--s-1)' }}>
              If an account exists for
            </p>
            <p style={{
              fontFamily: T.body,
              fontSize: 'var(--t-lead)',
              color: T.parchment,
              fontWeight: 500,
              margin: '0 0 var(--s-3)',
              wordBreak: 'break-word',
            }}>
              {formData.email}
            </p>
            <p className="rb-note" style={{ marginBottom: 'var(--s-4)' }}>
              a password reset link is on its way.
            </p>

            <button
              className="rb-btn rb-btn--gold"
              onClick={() => { setError(''); setView('login'); }}
            >
              Go to sign in
            </button>
          </div>
        )}
      </main>
    </div>
  );
}


// =============================================================================
// SMALL SHARED PIECES
// =============================================================================
// Local to this file: both are specific to a multi-view form flow, which no
// other pre-auth screen has. Anything used by more than one screen belongs in
// publicTheme.js instead.

/** Quiet back affordance above a form heading. */
function BackLink({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="rb-link rb-link--quiet"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        textDecoration: 'none',
        fontFamily: T.label,
        fontSize: 'var(--t-label)',
        fontWeight: 500,
        letterSpacing: '.18em',
        textIndent: '.18em',
        textTransform: 'uppercase',
        marginBottom: 'var(--s-4)',
        minHeight: '44px',
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.5" aria-hidden="true">
        <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {children}
    </button>
  );
}

/** The "we sent you mail" mark, shared by both confirmation views. */
function EnvelopeGlyph() {
  return (
    <div style={{
      width: '72px', height: '72px', borderRadius: '50%',
      background: `${T.gold}14`, border: `.5px solid ${T.gold}33`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto var(--s-3)',
    }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.gold}
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <path d="M22 6l-10 7L2 6" />
      </svg>
    </div>
  );
}
